// Brand copy — the strings that appear in more than one place.
//
// Split from `BrandLockup.tsx` for the same reason `mascots.ts` is split from
// `Mascot.tsx`: `eslint --max-warnings=0` enforces
// `react-refresh/only-export-components`, and a `.tsx` exporting both a
// component and plain data falls back to a full reload on every edit.
//
// **One slogan, one place.** It lands in at least four surfaces eventually —
// this lockup, the OG card tagline, the `meta description`, and the store
// listing. Four hand-typed copies is four chances to drift, and a drifting
// slogan is worse than no slogan: the product says two different things about
// itself in the tab bar and on a shared link.
//
// **Scope is deliberately narrow for now.** The user chose 2026-10-08 to let
// the slogan live on `/register` only, and to decide about the OG card and
// `meta description` after it has been read in the wild. So there is no
// `BRAND_LINE` export sitting here waiting to be used — an unused constant with
// a "TODO: use this later" comment is a placeholder, and `AGENTS.md §3 #4`
// bans those. The OG card and `index.html` still carry the older
// 「兩個人的私密時間容器」, which is a **known and accepted** inconsistency for
// now, not an oversight.
//
// The text is still provisional — `ADR-0129` records the decision and the
// alternatives — but changing it is a **single-line swap**, and
// `w46-brand-mark.test.ts` asserts every surface reads this constant rather
// than carrying its own copy.

/**
 * The romanised wordmark. **Uppercase** — changed 2026-10-08 from `heshe`,
 * at the user's request, on every surface at once.
 *
 * Uppercase is not only a spelling change here. Lowercase set at 17px rendered
 * as a small mark *beside* a word; set in caps at 20px it reads as one wordmark,
 * and the header lockup stops looking like an icon with a caption next to it.
 * The advance grows with it — measured in the browser at the lockup's 74px,
 * 700 weight, -1.5 tracking: lowercase is **2.836em**, uppercase **3.267em** — so
 * `scripts/build-brand-assets.mjs` carries the new figure for the standalone
 * lockup's text box. Carrying the old number over would have cropped the text.
 */
export const BRAND_WORDMARK = "HESHE";

/**
 * Slogan. 「一起記錄共同成長」 — the user's own framing of the product's core
 * value, kept verbatim rather than polished. Chosen 2026-10-08 after the first
 * round of candidates (which were all rejected in favour of *record together,
 * grow together*) and a second round of five written against it.
 *
 * Two deliberate details:
 *
 * - **成長, not 長大.** Both were on the table; `成長` is what was chosen, so it
 *   is what ships. Not an oversight.
 * - **No terminal punctuation.** The first version of the guard here asserted a
 *   fullwidth mark was *required*, which was backwards for this surface: a line
 *   set under a wordmark does not take a full stop, and dropping it also makes
 *   the line 8 characters instead of 9, which sits tighter in the lockup. What
 *   the guard now forbids is the **halfwidth** `,` and `.` — those are the
 *   keyboard-layout tell that the copy was typed in the wrong mode, and
 *   `simplified-text-audit.mjs` checks neither.
 *
 * `記錄` rather than `紀錄`: in Traditional Chinese `記錄` is "the act of writing
 * something down" and `紀錄` leans towards "a chronicle / document". A diary app
 * is doing the former.
 */
export const BRAND_SLOGAN = "一起記錄共同成長";
